import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Github, Twitter, ArrowUp, ArrowUpRight, Globe } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { useDynamicConfig } from "./DynamicConfig";

const FOOTER_UI_STRINGS: Record<string, Record<string, string>> = {
  en: {
    clocksTitle: "Global Operations Clocks",
    communications: "Communications",
    business: "Business:",
    careers: "Careers:",
    open: "Open",
    offline: "Offline"
  },
  es: {
    clocksTitle: "Relojes de operaciones globales",
    communications: "Comunicaciones",
    business: "Negocios:",
    careers: "Carreras:",
    open: "Abierto",
    offline: "Fuera de hora"
  },
  fr: {
    clocksTitle: "Horloges des opérations mondiales",
    communications: "Communications",
    business: "Affaires :",
    careers: "Carrières :",
    open: "Ouvert",
    offline: "Fermé"
  },
  de: {
    clocksTitle: "Globale Betriebsuhren",
    communications: "Kommunikation",
    business: "Geschäftlich:",
    careers: "Karriere:",
    open: "Geöffnet",
    offline: "Geschlossen"
  },
  hi: {
    clocksTitle: "वैश्विक संचालन घड़ियां",
    communications: "संचार केंद्र",
    business: "व्यवसाय:",
    careers: "करियर:",
    open: "सक्रिय",
    offline: "ऑफलाइन"
  },
  zh: {
    clocksTitle: "全球运营时钟",
    communications: "通讯与联络",
    business: "商业合作:",
    careers: "职业生涯:",
    open: "运营中",
    offline: "已下线"
  },
  ja: {
    clocksTitle: "グローバル運営時計",
    communications: "コミュニケーション",
    business: "ビジネス窓口:",
    careers: "採用情報:",
    open: "営業中",
    offline: "時間外"
  },
  ar: {
    clocksTitle: "ساعات العمليات العالمية",
    communications: "الاتصالات",
    business: "الأعمال:",
    careers: "الوظائف:",
    open: "مفتوح",
    offline: "مغلق"
  }
};

export default function Footer() {
  const { t, language } = useLanguage();
  const loc = FOOTER_UI_STRINGS[language as string] || FOOTER_UI_STRINGS["en"];
  const { logoText, logoChar } = useDynamicConfig();
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatHubTime = (timeZone: string) => {
    try {
      return time.toLocaleTimeString("en-US", {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (e) {
      return "--:--:--";
    }
  };

  const getOfficeStatus = (timeZone: string) => {
    const loc = FOOTER_UI_STRINGS[language as string] || FOOTER_UI_STRINGS["en"];
    try {
      const hr = parseInt(time.toLocaleTimeString("en-US", { timeZone, hour: "2-digit", hour12: false }), 10);
      if (hr >= 9 && hr < 18) {
        return { label: loc.open, color: "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" };
      }
      return { label: loc.offline, color: "bg-amber-400/80 shadow-[0_0_6px_rgba(245,158,11,0.1)]" };
    } catch (e) {
      return { label: loc.open, color: "bg-indigo-500" };
    }
  };

  const hubs = [
    { city: "New York", zone: "America/New_York", label: "US" },
    { city: "London", zone: "Europe/London", label: "UK" },
    { city: "Paris", zone: "Europe/Paris", label: "EU" },
    { city: "Dubai", zone: "Asia/Dubai", label: "ME" },
    { city: "Pune", zone: "Asia/Kolkata", label: "HQ" },
    { city: "Tokyo", zone: "Asia/Tokyo", label: "JP" },
    { city: "Sydney", zone: "Australia/Sydney", label: "AU" },
  ];

  const products = [
    { name: "Roomy™", href: "https://roomy.co.in" },
    { name: "SiteForces Builder", href: "https://siteforces.com" },
    { name: "Arrow Escape Mobile", href: "https://play.google.com/store/apps/details?id=com.teralumax.arrowescape.mzpqlk" }
  ];

  const company = [
    { name: t("aboutCompany", "navbar"), href: "#about-company" },
    { name: t("leadership", "navbar"), href: "#leadership" },
    { name: t("caseStudies", "navbar") || "Case Studies", href: "#cases" },
    { name: t("contact", "navbar"), href: "#contact" }
  ];

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-white border-t border-gray-150 py-16 md:py-20 overflow-hidden select-none">
      <div className="absolute inset-0 noise-overlay pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Top grid structure */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 pb-12 border-b border-gray-150 items-start">
          
          {/* Col 1: Branding block */}
          <div className="col-span-1 md:col-span-5 flex flex-col gap-5 text-left">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg border border-indigo-200 flex items-center justify-center">
                <span className="font-display font-bold text-xs text-indigo-600">{logoChar}</span>
              </div>
              <div className="flex flex-col text-left">
                <span className="font-display font-bold text-xs tracking-wider text-black uppercase">
                  {logoText}
                </span>
                <span className="text-[8.5px] font-mono tracking-widest text-gray-400 uppercase -mt-0.5">
                  Private Limited
                </span>
              </div>
            </div>
            
            <p className="text-gray-400 font-light text-xs max-w-sm leading-relaxed">
              {t("desc", "footer")}
            </p>

             <div className="mt-1 flex flex-col gap-1.5 text-left">
              <span className="text-[9px] font-mono tracking-widest text-brand-charcoal uppercase font-semibold">
                {loc.communications}
              </span>
              <div className="flex flex-col gap-2 mt-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-500 font-light font-sans text-[11px] min-w-[70px]">{loc.business}</span>
                  <a 
                    href="mailto:work@teralumax.com" 
                    className="font-mono text-gray-600 hover:text-indigo-600 transition-colors focus:outline-hidden py-2.5 px-4 bg-gray-50/60 hover:bg-indigo-50/40 rounded-lg border border-gray-150 min-h-[44px] inline-flex items-center text-xs"
                  >
                    work@teralumax.com
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-gray-500 font-light font-sans text-[11px] min-w-[70px]">{loc.careers}</span>
                  <a 
                    href="mailto:careers@teralumax.com" 
                    className="font-mono text-gray-600 hover:text-indigo-600 transition-colors focus:outline-hidden py-2.5 px-4 bg-gray-50/60 hover:bg-indigo-50/40 rounded-lg border border-gray-150 min-h-[44px] inline-flex items-center text-xs"
                  >
                    careers@teralumax.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Col 2: Products mappings */}
          <div className="col-span-1 md:col-span-3 flex flex-col gap-3.5 text-left">
            <span className="text-[10px] font-mono tracking-widest text-[#111827] uppercase font-semibold">
              {t("productsHeader", "footer")}
            </span>
            <ul className="flex flex-col gap-2">
              {products.map((p) => (
                <li key={p.name}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-gray-400 hover:text-indigo-600 transition-colors flex items-center gap-1 focus:outline-hidden"
                  >
                    {p.name} <ArrowUpRight size={10} className="opacity-0 hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Company mappings */}
          <div className="col-span-1 md:col-span-3 flex flex-col gap-3.5 text-left">
            <span className="text-[10px] font-mono tracking-widest text-[#111827] uppercase font-semibold">
              {t("companyHeader", "footer")}
            </span>
            <ul className="flex flex-col gap-2">
              {company.map((c) => (
                <li key={c.name}>
                  <a
                    href={c.href}
                    className="text-xs text-gray-400 hover:text-indigo-600 transition-colors focus:outline-hidden"
                  >
                    {c.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Top button trigger */}
          <div className="col-span-1 md:col-span-1 flex justify-end">
            <button
              onClick={handleScrollTop}
              aria-label="Scroll back layout to top"
              className="p-3 rounded-xl border border-gray-150 hover:border-black text-gray-400 hover:text-black hover:bg-neutral-50 transition-all focus:outline-hidden active:scale-95 cursor-pointer"
            >
              <ArrowUp size={16} />
            </button>
          </div>

        </div>

        {/* Global Operations Timezones Section */}
        <div className="border-t border-gray-150/60 mt-12 pt-8 select-none">
          <div className="flex flex-col gap-4 text-left">
            <div className="flex items-center gap-2">
              <Globe size={11} className="text-indigo-600 animate-pulse text-left" />
              <span className="text-[10px] font-mono tracking-widest text-[#111827] uppercase font-bold text-[#111827] text-left">
                {loc.clocksTitle}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
              {hubs.map((hub) => {
                const status = getOfficeStatus(hub.zone);
                return (
                  <div
                    key={hub.city}
                    className="p-3 bg-gray-50/50 hover:bg-indigo-50/10 border border-gray-100 rounded-xl transition-all duration-300 flex flex-col gap-1 hover:shadow-xs group"
                  >
                    <div className="flex items-center justify-between gap-1.5">
                      <span className="text-[11px] text-gray-800 font-medium group-hover:text-indigo-950 transition-colors">
                        {hub.city}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`w-1.5 h-1.5 rounded-full ${status.color} transition-colors duration-500`} title={status.label} />
                        <span className="text-[8px] font-mono uppercase tracking-wider text-gray-300 group-hover:text-gray-400 transition-colors">
                          {hub.label}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-mono text-gray-500 group-hover:text-indigo-600 transition-colors font-semibold">
                      {formatHubTime(hub.zone)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom copyright mapping rows */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-gray-400 text-[11px] font-mono">
          <div className="flex flex-wrap items-center gap-1.5 justify-center sm:justify-start">
            <span>© {new Date().getFullYear()} {logoText} Private Limited. {t("allRightsReserved", "footer")}</span>
            <a href="#YU3RzRWThZP&vHV=CfTY" className="text-[8px] text-gray-200 hover:text-indigo-600 transition-colors ml-0.5 select-none" title="Security Gateway Access Gate">●</a>
            <span className="hidden sm:inline text-gray-200">|</span>
            <span className="text-[9.5px] uppercase tracking-wide bg-gray-100 px-2 py-0.5 rounded-sm text-gray-600 border border-gray-200">
              REG_DELHI_IN
            </span>
          </div>

          {/* Social icons integration panel */}
          <div className="flex items-center gap-4.5">
            <a
              href="https://github.com"
              aria-label="Teralumax GitHub source"
              className="p-1 px-1.5 bg-gray-50/50 hover:bg-black hover:text-white border border-gray-150 rounded-md transition-all text-gray-400 focus:outline-hidden"
            >
              <Github size={13} />
            </a>
            <a
              href="https://twitter.com"
              aria-label="Teralumax Twitter stream"
              className="p-1 px-1.5 bg-gray-50/50 hover:bg-[#1DA1F2] hover:text-white border border-gray-150 rounded-md transition-all text-gray-400 focus:outline-hidden"
            >
              <Twitter size={13} />
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}
